import React, { useEffect, useState } from "react"
import {
  Col,
  FormGroup,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap"
import Flatpickr from "react-flatpickr"
import "flatpickr/dist/themes/material_blue.css"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { ErrorMessage, Field, Form, Formik } from "formik"
import * as Yup from "yup"
import CustomSingleSelect from "components/Shared/CustomSingleSelect"
import CustomLabel from "components/Common/CustomLabel"
import { editTrialPeriodAttendance } from "store/actions/trialPeriodAction"
import { showAlert } from "store/reducers/alertReducer"
import { format, parse } from "date-fns"
import { isSameDay, addDays } from "date-fns"

const initialValues = {
  attendance_id: null,
  status: null,
  reason_id: null,
  remarks: "",
  checkin_at: null,
  checkout_at: null,
}

const EditAttendanceModal = ({ isOpen, toggle, attendanceDetails }) => {
  const [hovered, setHovered] = useState(null)
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const { work_shifts, stations } = useSelector(state => state.Common)
  const { trialOperationsReasonList } = useSelector(state => state.TrialPeriods)
  const dispatch = useDispatch()
  const [formValues, setFormValues] = useState(initialValues)

  useEffect(() => {
    if (attendanceDetails) {
      setFormValues({
        attendance_id: attendanceDetails.id,
        status:
          attendanceDetails.checkin_at && attendanceDetails.checkout_at
            ? "absent"
            : "present",
        reason_id: null,
        remarks: null,
        checkin_at: attendanceDetails.checkin_at
          ? parse(
              attendanceDetails.checkin_at,
              "dd-MM-yyyy hh:mm a",
              new Date()
            )
          : null,
        checkout_at: attendanceDetails.checkout_at
          ? parse(
              attendanceDetails.checkout_at,
              "dd-MM-yyyy hh:mm a",
              new Date()
            )
          : null,
      })
    }
  }, [attendanceDetails])

  const attendanceTypes = {
    present: "Present",
    absent: "Absent",
  }

  const today = new Date(new Date().setHours(0, 0, 0, 0))
  const tomorrow = addDays(today, 1)

  const validationSchema = Yup.object().shape({
    status: Yup.string().required(t("Field is required")),
    remarks: Yup.string().required(t("Field is required")),

    reason_id: Yup.number().when("status", {
      is: val => val === "absent",
      then: () => Yup.number().required(t("Field is required")),
      otherwise: () => Yup.number().nullable(true),
    }),

    checkin_at: Yup.date()
      .nullable()
      .transform((value, originalValue) => {
        if (
          originalValue === null ||
          originalValue === "" ||
          originalValue === undefined
        )
          return null
        const parsed = new Date(originalValue)
        return isNaN(parsed) ? null : parsed
      })
      .nullable()
      .when("status", {
        is: val => val === "present",
        then: () =>
          Yup.date()
            .required(t("Field is required"))
            .test(
              "is-today-or-tomorrow",
              t("Date must be today or tomorrow"),
              value => {
                if (!value) return false
                return isSameDay(value, today) || isSameDay(value, tomorrow)
              }
            )
            .test(
              "is-valid-date",
              t("Invalid date"),
              value => value instanceof Date && !isNaN(value)
            ),
        otherwise: () => Yup.date().nullable(true),
      }),

    checkout_at: Yup.date()
      .transform((value, originalValue) => {
        if (
          originalValue === null ||
          originalValue === "" ||
          originalValue === undefined
        )
          return null
        const parsed = new Date(originalValue)
        return isNaN(parsed) ? null : parsed
      })
      .nullable()
      .when("status", {
        is: val => val === "present",
        then: () =>
          Yup.date()
            .nullable(true)
            .test(
              "is-valid-or-empty",
              t("Invalid date"),
              value =>
                value === null || (value instanceof Date && !isNaN(value))
            )
            .test(
              "is-today-or-tomorrow",
              t("Date must be today or tomorrow"),
              value => {
                if (!value) return true // allow empty/null
                return isSameDay(value, today) || isSameDay(value, tomorrow)
              }
            )
            .test(
              "is-30min-after-checkin",
              t("Check Out time must be at least 30 minutes after Check In"),
              function (checkout_at) {
                const { checkin_at } = this.parent
                if (!checkin_at || !checkout_at) return true
                const checkin = new Date(checkin_at)
                const checkout = new Date(checkout_at)
                const thirtyMinutes = 30 * 60 * 1000
                return checkout.getTime() - checkin.getTime() >= thirtyMinutes
              }
            ),
        otherwise: () => Yup.date().nullable(true),
      }),
  })

  const handleOnSubmit = values => {
    const {
      attendance_id,
      status,
      reason_id,
      remarks,
      checkin_at,
      checkout_at,
    } = values

    const payLoad = {
      attendance_id,
      status: status === "present" ? 1 : 2,
      reason_id: status === "present" ? null : reason_id,
      remarks,
      checkin_at:
        status === "present" ? format(checkin_at, "dd-MM-yyyy hh:mm a") : null,
      checkout_at:
        status === "present" ? format(checkout_at, "dd-MM-yyyy hh:mm a") : null,
    }
    dispatch(editTrialPeriodAttendance(payLoad))
      .then(action => {
        if (!action.payload) {
          return dispatch(
            showAlert({
              text: t(" invalid."),
              type: "error",
            })
          )
        }
        const { statusCode, success, message } = action.payload

        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }))
          toggle()
        } else {
          dispatch(showAlert({ text: message, type: "error" }))
        }
      })
      .catch(err => {
        console.log("err", err)
      })
  }

  const buttonStyle = (selected, text, isHovered) => {
    return {
      flex: "1 1 100px",
      margin: "4px",
      padding: "10px 15px",
      border: `1px solid  ${
        selected === text || isHovered
          ? "#00778a"
          : text === "present"
          ? "green"
          : text === "absent"
          ? "red"
          : "#99c1cd"
      }`,
      background: selected === text || isHovered ? "#00778a" : "white",
      color:
        selected === text || isHovered
          ? "white"
          : text === "absent"
          ? "red"
          : text === "present"
          ? "green"
          : "#5d98a0",
      fontSize: "14px",
      cursor: "pointer",
      fontWeight: selected === text ? "bold" : "normal",
      transition: "all 0.3s ease",
      borderRadius: "5px",
      minWidth: "120px",
    }
  }

  const reasonOptions = trialOperationsReasonList.map(item => ({
    value: item.id,
    label: item.reason,
  }))

  const shiftKey = Object.keys(work_shifts).find(key =>
    language === "en"
      ? work_shifts[key].name === attendanceDetails.shift
      : work_shifts[key].name_ar === attendanceDetails.shift
  )

  const stationKey = Object.keys(stations).find(key =>
    language === "en"
      ? stations[key].name === attendanceDetails.station
      : stations[key].name_ar === attendanceDetails.station
  )

  return (
    <Modal size="lg" isOpen={isOpen} toggle={toggle} centered>
      <Formik
        initialValues={formValues}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={handleOnSubmit}
      >
        {({ values, errors, touched, setFieldTouched,setFieldValue, isValid, dirty }) => (
          <Form>
            <>
              <ModalHeader toggle={toggle}>{t("Edit Attendance")}</ModalHeader>
              <ModalBody>
                <Row>
                  <Col xs={6} className="mb-2">
                    <div className="form_field">
                      <CustomLabel label={t("Shift")} />
                      <div className="d-flex flex-wrap">
                        {shiftKey && (
                          <button
                            className="main_btn fit_content min-w-max"
                            key={shiftKey}
                            name="shift"
                            style={{ minWidth: "100%", minHeight: "45px" }}
                            onClick={() => {}}
                          >
                            {t(
                              work_shifts[shiftKey][
                                language === "en" ? "name" : "name_ar"
                              ]
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </Col>
                  <Col xs={6} className="mb-2">
                    <div className="form_field">
                      <CustomLabel label={t("Station")} />
                      <div className="d-flex flex-wrap">
                        {stationKey && (
                          <button
                            className="main_btn fit_content"
                            key={stationKey}
                            name="station"
                            style={{ minWidth: "100%", minHeight: "45px" }}
                            onClick={() => {}}
                          >
                            {t(
                              stations[stationKey][
                                language === "en" ? "name" : "name_ar"
                              ]
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </Col>

                  <Col xs="12" sm="6" className="mb-1">
                    <div className="form_field">
                      <CustomLabel label={t("Date")} />
                      <Flatpickr
                        options={{
                          enableTime: true,
                          dateFormat: "d-m-Y",
                        }}
                        value={attendanceDetails.checked_at.split(" ")[0]}
                        disabled={true}
                        className="form-control"
                      />
                    </div>
                  </Col>

                  <Col xs="12" className="mb-3">
                    <div className="form_field">
                      <CustomLabel label={t("Attendance Type")} />
                      <div className="d-flex flex-wrap">
                        {Object.keys(attendanceTypes).map(option => (
                          <button
                            key={option}
                            type={"button"}
                            name="status"
                            onMouseEnter={() => setHovered(option)}
                            onMouseLeave={() => setHovered(null)}
                            style={buttonStyle(
                              values.status,
                              option,
                              hovered === option
                            )}
                            onClick={() => {
                              setFieldValue("status", option)
                            }}
                          >
                            {t(attendanceTypes[option])}
                          </button>
                        ))}
                      </div>
                    </div>
                  </Col>

                  {values.status === "present" ? (
                    <>
                      <Col xs="12" sm="6" className="mb-1">
                        <div className="form_field">
                          <CustomLabel
                            label={t("Select Check In Date and Time")}
                          />
                          <Flatpickr
                            options={{
                              enableTime: true,
                              dateFormat: "d-m-Y h:i K",
                              minDate: "today",
                              enable: [
                                function (date) {
                                  const today = new Date()
                                  today.setHours(0, 0, 0, 0)
                                  date.setHours(0, 0, 0, 0)
                                  return date.getTime() === today.getTime()
                                },
                              ],
                            }}
                            value={values.checkin_at}
                            // disabled={attendanceDetails.checkin_at !== ""}
                            onChange={selectedDates => {
                              setFieldValue("checkin_at", selectedDates[0])
                              setFieldTouched("checkin_at", true)
                            }
                            }
                            className="form-control"
                          />
                        </div>
                        <ErrorMessage
                          name={"checkin_at"}
                          component="div"
                          className="text-danger"
                        />
                      </Col>

                      <Col xs="12" sm="6" className="mb-1">
                        <div className="form_field">
                          <CustomLabel
                            label={t("Select Check Out Date and Time")}
                          />
                          <Flatpickr
                            options={{
                              enableTime: true,
                              dateFormat: "d-m-Y h:i K",
                              minDate: "today",
                              enable: [
                                function (date) {
                                  const today = new Date()
                                  const tomorrow = new Date()
                                  tomorrow.setDate(today.getDate() + 1)

                                  // Reset hours/minutes/seconds for comparison
                                  today.setHours(0, 0, 0, 0)
                                  tomorrow.setHours(0, 0, 0, 0)
                                  date.setHours(0, 0, 0, 0)

                                  return (
                                    date.getTime() === today.getTime() ||
                                    date.getTime() === tomorrow.getTime()
                                  )
                                },
                              ],
                            }}
                            value={values.checkout_at}
                            // disabled={attendanceDetails.checkout_at !== ""}
                            onChange={selectedDates => {
                              setFieldValue("checkout_at", selectedDates[0])
                              setFieldTouched("checkout_at", true)
                            }}
                            className="form-control"
                          />
                        </div>
                        <ErrorMessage
                          name={"checkout_at"}
                          component="div"
                          className="text-danger"
                        />
                      </Col>
                    </>
                  ) : (
                    values.status === "absent" && (
                      <Col md={6} xs="12" className="mb-3">
                        <div className="form_field">
                          <CustomLabel label={t("Reason")} isRequired />
                          <CustomSingleSelect
                            value={reasonOptions.find(
                              item => item.value === values.reason
                            )}
                            options={reasonOptions}
                            onChange={e => {
                              setFieldValue("reason_id", e?.value)
                              setFieldTouched("reason_id", true)
                            }}
                            placeholder={t("Select reason")}
                            name="reason"
                            // onBlur={handleBlur}
                          />
                        </div>
                        <ErrorMessage
                          name={"reason"}
                          component="div"
                          className="text-danger"
                        />
                      </Col>
                    )
                  )}

                  <Col lg={12}>
                    <div className="form_field">
                      <CustomLabel
                        htmlFor="remarks"
                        label={t("Remarks")}
                        isRequired
                      />
                      <Field
                        as="textarea"
                        name="remarks"
                        className="form-control"
                        placeholder={t("Enter Remarks")}
                        maxLength={250}
                      ></Field>
                    </div>
                    <ErrorMessage
                      name={"remarks"}
                      component="div"
                      className="text-danger"
                    />
                  </Col>
                </Row>
              </ModalBody>
              <ModalFooter>
                <div className="d-flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="main_btn disabled-button fit_content px-4"
                    onClick={toggle}
                  >
                    {t("Cancel")}
                  </button>
                  <button
                    type="submit"
                    className="main_btn fit_content px-4"
                    disabled={!isValid}
                  >
                    {t("Save")}
                  </button>
                </div>
              </ModalFooter>
            </>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}

export default EditAttendanceModal
